/* eslint-disable no-param-reassign */

import { pick, flatten } from 'lodash-es';

const jsondiffpatch = require('jsondiffpatch');

const clone = (obj) => JSON.parse(JSON.stringify(obj));

// Create a timeline patcher that uses timeline_id to match array items
const diffpatcher = jsondiffpatch.create({
  objectHash: (obj, index) => (
    obj?.timeline_id || `$$index:${index}`
  ),
});

// Track previous state of production before user changes things.
// Used to calculate delta
let prevProduction;

// helper functions that need a new home sometime in the future
const getItemFromTimeline = (timelineId, timeline) => {
  if (!timeline?.length) {
    return null;
  }

  const index = timeline.findIndex((item) => item.timeline_id === timelineId);
  return timeline[index];
};

const normalizeType = (layout) => ({
  graphic: 'Graphic',
  panel: 'Panel',
  theme: 'Theme',
}[layout] || 'Theme');

const isTheme = (layout) => !['graphic', 'graphics', 'panel', 'asset', 'poll'].includes(layout);

const isAlreadyQueued = (parent, post, timeline) => {
  const item = getItemFromTimeline(parent, timeline);
  return (item?.queuedPost === post);
};

/**
 * Checks if timeline item is a social graphic,
 * and returns the social element's fields map if there is one as an array of length 1.
 * If item is a graphics group, it will return an array of all social element field maps found.
 * @param {Object} item - Timeline item
 * @returns {Array<Object>} - List of field maps for all social graphic layers found.
 */
const getSocialFields = (item) => {
  if (item.layout === 'graphic' && item.socialData?.length) {
    const [id] = item.socialData;
    return [item.fields[id]];
  }

  if (item.layout === 'graphics') {
    return item.posts.reduce((memo, post) => {
      if (post.socialData?.length) {
        const [id] = post.socialData;
        memo.push(post.fields[id]);
      }

      return memo;
    }, []);
  }

  return [];
};

/**
 * Checks if timeline item contains any social posts that are selected.
 * If a social post is queued, then set the sub_index to that post's timeline_id.
 * Otherwise the sub_index will just match the index.
 * @param {String} timelineId - ID of the live element to get the sub_index for.
 * @param {Array<Object>} timeline - Full production timeline.
 * @returns {String} sub_index to use
 */
const determineSubIndex = (item) => {
  if (!item) {
    return null;
  }

  const {
    posts,
    queuedPost,
  } = item;

  const [post] = posts || [];

  // If there's a social post and it's a tagboard...
  if (post?.type === 'tagboard') {
    // then return the tagboard's timeline_id
    return post.timeline_id;
  }

  // If the timeline item has a queuedPost, then use that.
  // This only covers queued posts for themes.
  // NOTE: Shouldn't have to check layout here; idk how queuedPost gets set on graphics groups
  if (item.layout !== 'graphics' && queuedPost) {
    return queuedPost;
  }

  // If timeline item is a social graphic,
  // then we need to check fields for a queuedPost
  const socialFields = getSocialFields(item);
  if (socialFields.length) {
    // Look for the first queuedPost we can find if there is one
    for (let i = 0; i < socialFields.length; i += 1) {
      const fields = socialFields[i] || {};
      const [p] = fields.posts || [];

      // Ignore graphics with full tagboards in them
      if (p?.type !== 'tagboard' && fields.queuedPost) {
        return fields.queuedPost;
      }
    }
  }

  return item.timeline_id;
};

/**
 * Ensure timeline item isn't an empty object and contains `posts` key
 */
const validateTimelineItem = (item) => (
  item
  && Object.keys(item).length
  && item.posts
);

const getChanges = (production) => diffpatcher.diff(prevProduction, production);

/**
 * Merge provided object into prevProduction.
 * Mainly used to catch prevProduction up with any changes,
 * but not completely overwriting it.
 * @param {Object} partial - Object to merge into prevProduction
 */
const updatePrevProduction = (partial) => {
  prevProduction = Object.assign(prevProduction, partial);
};

/**
 * Overwrite prevProduction with a clone of the provided production
 * @param {Object} production - Production to clone and store
 */
const setPrevProduction = (production) => {
  prevProduction = clone(production);
};

/**
 * Get list of social posts attached to the timeline item.
 * For themes and single graphics, this is just the array of posts found on them.
 * For grouped graphics, returned value will be a concatenated list of all social posts
 * found in all graphics in the group.
 * @param {Object} item - Timeline item
 * @returns {Array<Object>} List of social posts and/or tagboards
 */
const getSocialPosts = (item) => {
  if (!item) {
    return [];
  }

  switch (item.layout) {
    case 'graphic': {
      const [socialId] = item.socialData || [];
      const fields = item.fields[socialId] || {};
      return fields?.posts || [];
    }

    case 'graphics': {
      return flatten(
        item.posts.map((it) => getSocialPosts(it)),
      );
    }

    default: {
      return item.posts;
    }
  }
};

let updateTimelineTimeout;

/**
 * Calculate diff of production and send changes over the socket
 * @param {Object} state
 * @param {Object} rootState
 * @param {boolean} [preview]
 */
const updateTimeline = (state, rootState, opts = {}) => {
  const {
    preview = false,
    force = false,
  } = opts;

  if (window._tgbSaving) {
    // Wait and try again in 0.1s
    clearTimeout(updateTimelineTimeout);
    updateTimelineTimeout = setTimeout(() => {
      updateTimeline(state, rootState, opts);
    }, 100);

    return;
  }

  // Global variable used for integration tests
  window._tgbSaving = true;

  try {
    const prodIndex = preview ? preview.index : state.production.index;
    const layout = state.production.timeline.find((item) => (
      item.timeline_id === prodIndex
    ));

    const prodSubIndex = preview ? preview.sub_index : state.production.sub_index;

    const posts = getSocialPosts(layout);

    const post = (posts || []).find((p) => (
      p.timeline_id === prodSubIndex
    ));

    const live = {
      layout,
      post,
    };

    // TODO: We should probably merge fields into settings for smart panels
    // tgb-live will do it too, but for backwards compatibility with old displays...

    if (isTheme(live.layout?.layout) && live.post) {
      // We'll attach whatever post we have here in producer,
      // but tgb-live shouldn't rely on it.
      // It should still fetch the post if the data is missing.
      const postRecord = rootState.posts.lookupTable[post.post_id];
      if (post?.post_id && postRecord) {
        // Giving this info to tgb-live will save us a network request,
        // but tgb-live shouldn't rely on it
        live.post = {
          ...post,
          ...postRecord,

          // This flag tells live to not bother loading the post
          loaded: true,
        };
      }
    }

    let delta;
    if (!preview) {
      delta = diffpatcher.diff(prevProduction, state.production);
      setPrevProduction(state.production);
    }

    // When data is sent through here with preview = true
    // the data will be sent to the updatePreviewTimeline action
    // this way it'll update the connected screens on the preview room
    const data = {
      production_id: state.production._id,
      owner: state.production.owner,
      user_id: rootState.user.id,
      delta,
      force,

      // NOTE: `live` is redundant since outputs can deduce the live layout using the timeline
      //       Remove whenever tgb-live is no longer reliant on it
      live,
      display_token: state.production.display_token,
    };

    if (preview) {
      data.preview = true;

      // Gotta attach these since preview doesn't handle deltas the same way
      data.index = preview.index;
      data.sub_index = preview.sub_index;
      data.videoPlaying = preview.videoPlaying;

      // NOTE: It sucks to send the entire timeline again,
      // but easier than setting up two separate deltas (for now)
      data.timeline = state.production.timeline;
    }

    vm.$socket.emit('updateTimeline', data);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('Error updating timeline:', err);
  } finally {
    // Global variable used for integration tests
    window._tgbSaving = false;
  }
};

const sendSettings = (state) => {
  const { production } = state;

  vm.$socket.emit(
    'settings',
    {
      production_id: production._id,
      ...pick(production, [
        'playing',
        'videoPlaying',
        'muted',
        'looped',
        'backgroundColor',
        'backgroundType',
        'backgroundImage',
        'backgroundVideo',
        'transition',
        'transitionMode',
      ]),
    },
  );
};

/**
 * Get previous item and/or post given the current index and subIndex.
 * @param {Object[]} opts.timeline - Current production timeline
 * @param {string} opts.index - timeline_id of current live item
 * @param {string} opts.subIndex - timeline_id of current live post (nested inside item)
 * @param {boolean} opts.looped - Whether or not to loop around when at the start
 * @returns {Object} Object with previous item/post index and subIndex
 */
const getPreviousItemOrPost = (opts = {}) => {
  const {
    timeline,
    index,
    subIndex,
    looped,
  } = opts;

  const itemCount = timeline.length;

  const currentItemIdx = timeline.findIndex((i) => i.timeline_id === index);
  const currentItem = timeline[currentItemIdx];

  const posts = getSocialPosts(currentItem);
  const currentPostIdx = (index !== subIndex)
    ? posts?.findIndex((p) => p.timeline_id === subIndex)
    : -1;

  const prev = {
    index: (() => {
      if (currentPostIdx > 0) {
        // Stay on current item if not on first post
        return index;
      }

      if (currentItemIdx === 0) {
        if (!looped) {
          // Stay right here
          return index;
        }

        // Loop around to last timeline item
        return timeline[itemCount - 1].timeline_id;
      }

      // Move back one item
      return timeline[currentItemIdx - 1].timeline_id;
    })(),

    subIndex: (() => {
      if (currentPostIdx > 0) {
        // Move back one post
        return posts[currentPostIdx - 1].timeline_id;
      }

      if (currentItemIdx === 0 && !looped) {
        // Stay right here, since looping is disabled
        return subIndex;
      }

      const prevItem = currentItemIdx === 0
        ? timeline[itemCount - 1]
        : timeline[currentItemIdx - 1];

      // We know something has nested posts if they have different timeline_ids
      // Graphics groups uses `posts`, but they all render together instead of sequentially
      const prevPosts = getSocialPosts(prevItem);
      if (!prevPosts?.length) {
        // Just set sub_index to same value as index
        return prevItem.timeline_id;
      }

      // Set to final post on previous timeline item (if applicable)
      return prevPosts[prevPosts.length - 1].timeline_id;
    })(),
  };

  return prev;
};

/**
 * Get next item and/or post given the current index and subIndex.
 * @param {Object[]} opts.timeline - Current production timeline
 * @param {string} opts.index - timeline_id of current live item
 * @param {string} opts.subIndex - timeline_id of current live post (nested inside item)
 * @param {boolean} opts.looped - Whether or not to loop around to the first item
 * @returns {Object} Object with next item/post index and subIndex
 */
const getNextItemOrPost = (opts = {}) => {
  const {
    timeline,
    index,
    subIndex,
    looped,
  } = opts;

  const itemCount = timeline.length;

  // Timeline Item + Index
  const currentItemIdx = timeline.findIndex((i) => i.timeline_id === index);
  const currentItem = timeline[currentItemIdx];

  const posts = getSocialPosts(currentItem);
  const postCount = posts.length;

  // Post Index
  const currentPostIdx = (index !== subIndex)
    ? posts.findIndex((p) => p.timeline_id === subIndex)
    : -1;

  const next = {
    index: (() => {
      if (currentPostIdx >= 0 && currentPostIdx < postCount - 1) {
        // Moving to next post, so stay on same item
        return index;
      }

      if (currentItemIdx >= itemCount - 1) {
        if (!looped) {
          // No looping, so stay right here
          return index;
        }

        // Wrap around to beginning
        return timeline[0].timeline_id;
      }

      // Move to next timeline item
      return timeline[currentItemIdx + 1].timeline_id;
    })(),

    subIndex: (() => {
      if (currentPostIdx >= 0 && currentPostIdx < postCount - 1) {
        // Move forward one post
        return posts[currentPostIdx + 1].timeline_id;
      }

      if (!looped && currentItemIdx >= itemCount - 1) {
        // Just stay on final item and final post
        return subIndex;
      }

      const nextItem = currentItemIdx < itemCount - 1
        ? timeline[currentItemIdx + 1] // Next item in playlist
        : timeline[0]; // Loop back to beginning

      // We know something has nested posts if they have different timeline_ids
      // Graphics groups uses `posts`, but they all render together instead of sequentially
      const nextPosts = getSocialPosts(nextItem);

      if (!nextPosts?.length) {
        // Just set sub_index to same value as index
        return nextItem.timeline_id;
      }

      // Set to first post on next timeline item (if applicable)
      return nextPosts[0].timeline_id;
    })(),
  };

  return next;
};

/**
 * Update `queuedPost` on whatever is currently live, if `sub_index` doesn't match.
 * Applies to social graphics and themes with individual posts.
 * Updates the timeline item in place.
 * @param {Array<Object>} opts.timeline - Current production timeline
 * @param {String} opts.index - Current live layout ID
 * @param {String} opts.subIndex - Current live post (when applicable).
 * @returns {Boolean} True if timeline was updated.
 */
const syncQueuedPost = (opts = {}) => {
  const {
    index,
    subIndex,
    timeline,
  } = opts;

  if (!index || index === subIndex) {
    return false;
  }

  // If sub_index is different from index,
  // and it's a graphic then we should update queuedPost on the graphic
  const item = getItemFromTimeline(index, timeline);

  switch (item?.layout) {
    case 'graphics': {
      // Loop over child graphics, reset queuedPost to null
      // then set queuedPost to the proper post if the graphic contains
      // the new post (i.e. matches sub_index)
      for (let i = 0; i < item.posts.length; i += 1) {
        const graphic = item.posts[i];

        const [id] = graphic.socialData || [];
        const fields = graphic.fields[id];

        if (fields && fields.queuedPost !== subIndex) {
          const { posts } = fields;

          if (posts && posts.some((p) => p.timeline_id === subIndex)) {
            fields.queuedPost = subIndex;
          }
        }
      }

      break;
    }

    case 'graphic': {
      const [id] = item.socialData || [];
      const fields = item.fields[id];

      if (fields && fields.queuedPost !== subIndex) {
        fields.queuedPost = subIndex;
        return true;
      }

      break;
    }

    default: {
      if (item && isTheme(item.layout) && item.queuedPost !== subIndex) {
        item.queuedPost = subIndex;
        return true;
      }

      break;
    }
  }

  return false;
};

export {
  determineSubIndex,
  diffpatcher,
  getChanges,
  getItemFromTimeline,
  getNextItemOrPost,
  getPreviousItemOrPost,
  getSocialPosts,
  isAlreadyQueued,
  isTheme,
  normalizeType,
  sendSettings,
  setPrevProduction,
  syncQueuedPost,
  updatePrevProduction,
  updateTimeline,
  validateTimelineItem,
};
